import {CommonModule, DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Component, EventEmitter, Inject, Input, OnDestroy, Output, PLATFORM_ID} from '@angular/core';
import {Router, RouterModule} from '@angular/router';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {ThumbnailModule} from '../../thumbnail/thumbnail.module';
import {PipesModule} from '../../../core/pipes/pipes.module';
import {PostTypeEnum} from '../../../core/enums/post-type.enum';
import {MediaTypeEnum} from '../../../core/enums/media-type.enum';
import {EventShowTabsEnum} from '../../../features/event/event-show/event-show-tabs.enum';
import {PostViewModel} from '../post.view-model';
import {AvatarAction, AvatarActionsComponent} from '../../modal/components/avatar-actions/avatar-actions.component';

@Component({
  selector: 'app-post-source-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    ThumbnailModule,
    PipesModule,
    ButtonModule,
    DialogModule,
    AvatarActionsComponent,
  ],
  templateUrl: './post-source-header.component.html',
  styleUrl: './post-source-header.component.scss'
})
export class PostSourceHeaderComponent implements OnDestroy {
  @Input() post: PostViewModel | null = null;
  @Input() showShareState = false;
  @Input() showCollaborationTarget = false;
  @Output() authorChoiceVisibleChange = new EventEmitter<boolean>();
  @Output() navigationRequested = new EventEmitter<void>();

  public authorChoiceMounted = false;
  public authorChoiceVisible = false;
  private authorChoiceSlug: string | null = null;

  // PrimeNG 17 forwards Escape to every open dialog. Capture it while this
  // chooser exists so dismissing it cannot also close the gallery underneath.
  private readonly authorChoiceKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.authorChoiceVisible = false;
    }
  };

  public readonly PostTypeEnum = PostTypeEnum;
  public readonly EventShowTabsEnum = EventShowTabsEnum;

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) { }

  public ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.document.removeEventListener('keydown', this.authorChoiceKeydown, true);
    }
  }

  public get canChooseAuthorDestination(): boolean {
    return !!this.post?.postedBy?.slug && !this.post.postedBy.deleted;
  }

  public openAuthorChoice(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (!isPlatformBrowser(this.platformId) || !this.canChooseAuthorDestination || this.authorChoiceMounted) {
      return;
    }
    this.authorChoiceSlug = this.post.postedBy.slug;
    this.authorChoiceMounted = true;
    this.authorChoiceVisible = true;
    this.document.addEventListener('keydown', this.authorChoiceKeydown, true);
    this.authorChoiceVisibleChange.emit(true);
  }

  public onAuthorChoiceHidden(): void {
    this.document.removeEventListener('keydown', this.authorChoiceKeydown, true);
    this.authorChoiceMounted = false;
    this.authorChoiceVisible = false;
    this.authorChoiceSlug = null;
    this.authorChoiceVisibleChange.emit(false);
  }

  public chooseAuthorDestination(action: AvatarAction): void {
    const slug = this.authorChoiceSlug;
    if (!slug || !this.authorChoiceVisible || (action !== 'profile' && action !== 'garage')) {
      return;
    }
    this.authorChoiceVisible = false;
    this.navigationRequested.emit();
    void this.router.navigate(['/', slug, action]);
  }

  public onResourceLinkClick(event: Event): void {
    event.stopPropagation();
    this.navigationRequested.emit();
  }

  public get hasVideoMedia(): boolean {
    return !!this.post?.media?.some((media) => media?.type === MediaTypeEnum.VIDEO);
  }

  public get hasResourceTarget(): boolean {
    return !!this.post && (
      ((this.post.type === PostTypeEnum.PROJECT_POST || this.post.type === PostTypeEnum.PROJECT_MEDIA_POST)
        && !!this.post.project)
      || (this.post.type === PostTypeEnum.EVENT_DISCUSSION_POST && !!this.post.event)
      || (this.showCollaborationTarget && this.post.type === PostTypeEnum.COLLABORATION_MEDIA_POST
        && !!this.post.collaboration)
    );
  }

  public get projectTargetLabel(): string {
    return this.post?.project?.displayVehicleIdentity || '';
  }

  public get collaborationProjectTargetLabel(): string {
    return this.post?.collaboration?.project?.displayVehicleIdentity
      || this.post?.collaboration?.project?.vehicle?.fullVehicleIdentity
      || '';
  }
}
